import React, { useState, useEffect } from "react";
import axios from "axios"; // Used for feedback API calls
// ⚠️ DEPRECATED IMPORTS REMOVED - Now using pre-calculated values from API
// Old: import { calculateAllSectionScores, calculatePercentilesWithQuestionTypes } from '../utils/scoringUtils';
import aiContextService from '../utils/aiContextService';
import testAttemptDateService from '../utils/testAttemptDateService';
import aiPromptEnhancer from '../utils/aiPromptEnhancer';
import StudentIdUtils from '../utils/studentIdUtils';
import aiSummaryCacheService from '../utils/aiSummaryCache';

// Get the API base URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// score-card.js
const fetchStoredSummary = async (student, test, modelTypeParam) => { 
  if (!student || !test || !modelTypeParam) { // Basic validation
      console.warn("fetchStoredSummary called with missing parameters", { student, test, modelTypeParam });
      return null;
  }
  try {
    const res = await axios.get(`${API_BASE_URL}/summary`, {
      params: {
        student,
        test,
        skillType: "score_insights", // This is hardcoded, ensure it's correct for your use case
        responseType: "summary",    // This is hardcoded, ensure it's correct
        modelType: modelTypeParam,    // <--- MODIFIED: Use the passed parameter
      },
    });
    return res.data?.content || null; // If content exists, return it
  } catch (error) {

    if (error.response && error.response.status === 404) {
    } else {
      console.error(`Error fetching stored summary for ${modelTypeParam}:`, error.response?.data || error.message);
    }
    return null;
  }
};

const saveGeneratedSummary = async (student, test, content, modelType = "chatgpt") => {
  try {
    console.log(`Saving summary for: ${student}, ${test}, ${modelType}`); // ✅ DEBUG
    await axios.post(`${API_BASE_URL}/summary`, {
      student,
      test,
      skillType: "score_insights",
      responseType: "summary",
      content,
      modelType,
      timestamp: new Date().toISOString(),
    });
  } catch (err) {
    console.error("Failed to save summary", err);
  }
};

// Static cutoff scores REMOVED — project rule: "No fallback percentile cutoffs in production".
// Cutoffs MUST come from the backend via the `categoryCutoffs` prop (resolved from
// MongoDB test_cutoffs + unified_cutoffs.json → percentile API). When absent, UI
// must display "UNKNOWN"/"N/A" rather than fabricating values (previously 25/20/20/80).

// Sample bullet point insights for training the AI
const SAMPLE_INSIGHTS_FOR_TRAINING = [
  "• This is your first SimCAT for CAT 20XX and you have done a decent job clearing the cut-off on the Quant section.\n• Your level on each of the sections is Learner, Learner, and Capable.\n• Overall you are at a Learner Level with good potential to improve.\n• You will find your detailed plan of action in the Skill Insights section.",
  "• You've cleared the Verbal cut-off for the first time! Congratulations!\n• You maintained your strong performance on Quant and DI sections.\n• Your scores on these sections are very close to your previous best scores.\n• Continue focusing on maintaining consistency across all sections.",
  "• Your overall performance is consistent with your average in recent tests.\n• The Verbal Section on this test was notably tougher than usual SimCATs.\n• You performed better than usual in Quant and DILR sections.\n• Focus on improving your Verbal strategy for the upcoming tests.",
];

const ScoreCard = ({ questionsData, students, tests, selectedStudent, selectedTest, percentileData, studentCategory, categoryCutoffs }) => {
  const [elaborateData, setElaborateData] = useState([]);
  const [catReplicaData, setCatReplicaData] = useState([]);
  const [graphicData, setGraphicData] = useState(null);
  // categoryCutoffs now comes from props (passed from backend response)
  
  const [aiInsights, setAiInsights] = useState({
    deepseek: ""
  });
  const [loadingAI, setLoadingAI] = useState({
    deepseek: false
  });

  // Debug state for AI context visibility
  const [debugInfo, setDebugInfo] = useState({
    historicalTests: [],
    contextString: '',
    apiResponseTime: 0,
    currentTestDate: null,
    isFirstTest: null,
    error: null
  });
  const [showDebug, setShowDebug] = useState(false);

  const [testHistory, setTestHistory] = useState([]);
  const [testAttemptDates, setTestAttemptDates] = useState([]);
  const [currentTestAttemptDate, setCurrentTestAttemptDate] = useState(null);
  // REMOVED: Test names now come directly from API, no separate service needed
  const [testNames, setTestNames] = useState({});

  const [percentiles, setPercentiles] = useState({});
  const [allTestPercentiles, setAllTestPercentiles] = useState({});
  const [allTestScores, setAllTestScores] = useState({});
  const [backendCutoffs, setBackendCutoffs] = useState(null); // Store cutoffs from backend response

  useEffect(() => {
    // Test names now come from API data directly
    setTestNames({});
  }, []);

  // REMOVED: No longer fetching cutoffs via API call
  // Cutoffs are already provided by backend in the AI insights response
  // They come via detailedData.overall.cutoff and detailedData.sections[].cutoff



  // Helper function to safely get test name
  const getTestNameSafely = (testId) => {
    if (!testId) return 'Unknown Test';
    
    // First try from cached test names
    let testName = testNames[testId] || 
                   testNames[`Test_${testId}`] || 
                   testNames[testId.replace('Test_', '')];
    
    // If not found, return formatted test ID
    return testName || `Test ${testId}`;
  };

  useEffect(() => {
    if (!questionsData || questionsData.length === 0) {
        setTestHistory([]);
        setTestAttemptDates([]);
        return;
    }
    const orderedTests = [...new Set(questionsData.map(item => item.test_name))];
    setTestHistory(orderedTests); 

    // Fetch test attempt dates when student is selected
    const fetchAttemptDates = async () => {
      if (selectedStudent) {
        try {
          // Extract IMS PIN from selectedStudent (remove student_ prefix)
          const studentPin = selectedStudent.replace('student_', '');
          console.log('📅 ScoreCard: Fetching attempt dates for student:', studentPin);
          
          const attemptDates = await testAttemptDateService.getStudentTestAttemptDates(studentPin);
          setTestAttemptDates(attemptDates || []);
          
          // Get current test attempt date
          if (selectedTest && attemptDates) {
            const currentDate = attemptDates.find(date => date.testId === selectedTest);
            setCurrentTestAttemptDate(currentDate?.formattedDate || null);
            console.log('📅 ScoreCard: Current test attempt date:', currentDate?.formattedDate);
          }
        } catch (error) {
          console.error('❌ ScoreCard: Error fetching attempt dates:', error);
          setTestAttemptDates([]);
          setCurrentTestAttemptDate(null);
        }
      } else {
        setTestAttemptDates([]);
        setCurrentTestAttemptDate(null);
      }
    };
    
    fetchAttemptDates();

    if (!selectedStudent || !selectedTest) {
        setElaborateData([]);
        setCatReplicaData([]);
        setGraphicData(null);
        setAiInsights({ deepseek: "" });
        setCurrentTestAttemptDate(null);
        return;
    }
    
    const testData = questionsData.filter(r => {
      // The backend now returns test_name as the testId for consistent filtering
      // selectedTest is the testId (e.g., "68259b7ffefcf4e8650e219b")
      // test_name should now match selectedTest directly
      
      const matchesDirectTest = r.test_name === selectedTest;
      const matchesPrefixedTest = r.test_name === `Test_${selectedTest}`;
      
      console.log('🔍 ScoreCard: Test filtering debug:', {
        questionTestName: r.test_name,
        questionTestTitle: r.test_title,
        selectedTest,
        testWithPrefix: `Test_${selectedTest}`,
        matchesDirectTest,
        matchesPrefixedTest
      });
      
      return matchesDirectTest || matchesPrefixedTest;
    });
    
    console.log('📊 ScoreCard: After filtering test data:', {
      originalDataLength: questionsData.length,
      filteredDataLength: testData.length,
      selectedTest,
      sampleOriginalData: questionsData.slice(0, 2).map(d => ({ test_name: d.test_name, test_title: d.test_title, section: d.section })),
      sampleFilteredData: testData.slice(0, 2).map(d => ({ test_name: d.test_name, test_title: d.test_title, section: d.section }))
    });
    const sections = ["QA", "VARC", "DILR"];
    
    processScoreData(testData, sections, selectedStudent, orderedTests);
  }, [selectedStudent, selectedTest, questionsData]);

  // ⚠️ UPDATED: Now uses pre-calculated values from API instead of calculating
  // The backend provides:
  // - analyticsData.summary.marks_obtained (total score)
  // - analyticsData.sections[].marks_obtained (section scores)
  // - percentileData (pre-calculated percentiles from MyIMSAdmin)
  //
  // We NO LONGER calculate scores or percentiles on the frontend.
  // All values come pre-calculated from MyIMSAdmin database.
  
  const calculateAllTestPercentilesAndScores = (student) => {
    // This function is now simplified to just extract values from questionsData
    // NO calculations performed - data is already pre-calculated
    
    const allUniqueTests = [...new Set(questionsData.map(item => item.test_name))];
    const sections = ["QA", "VARC", "DILR"];
    const scoresByTest = {};
    
    // For current test, we use the percentileData prop which comes from API
    // For other tests, we would need to fetch their data separately
    // For now, we only have pre-calculated data for the selected test
    
    allUniqueTests.forEach(test => {
      const testDataForCalc = questionsData.filter(r => {
        return r.test_name === test || r.test_name === `Test_${test}` || `Test_${r.test_name}` === test;
      });
      
      // Extract scores from data (already calculated in backend)
      // Note: For multi-test comparison, we'd need to call the API for each test
      scoresByTest[test] = extractScoresFromData(testDataForCalc, sections, student);
    });

    // Return scores and use percentileData prop for current test percentiles
    return { 
      percentiles: {}, // Percentiles come from percentileData prop, not calculated here
      scores: scoresByTest 
    };
  };

  // Extract pre-calculated scores from test data
  // NOTE: Backend now provides student_marks field which contains pre-calculated marks
  // for each question (+3, -1, 0) based on MCQ/TITA question type
  const extractScoresFromData = (testData, sections, studentPin) => {
    const scores = {};
    
    sections.forEach(section => {
      const sectionQuestions = testData.filter(q => q.section === section);
      
      // Sum up student_marks which are pre-calculated by backend
      // Backend calculates these based on:
      // - MCQ: +3 correct, -1 incorrect, 0 skipped
      // - TITA: +3 correct, 0 incorrect/skipped
      scores[section] = sectionQuestions.reduce((sum, q) => {
        // Try to get marks from student_marks field (pre-calculated by backend)
        let marks = 0;
        
        if (q.student_marks !== undefined && q.student_marks !== null) {
          marks = parseFloat(q.student_marks);
        } else {
          // Fallback: calculate from response if student_marks not available
          // This shouldn't happen with new API but keeping as safety
          const response = q[studentPin];  // Use clean PIN
          if (response === 'correct') {
            marks = 3;
          } else if (response === 'incorrect') {
            // Would need question_type to know if TITA (0) or MCQ (-1)
            // For now assume MCQ
            marks = q.question_type === 'TITA' ? 0 : -1;
          } else {
            marks = 0; // skipped
          }
        }
        
        return sum + (isNaN(marks) ? 0 : marks);
      }, 0);
    });
    
    // Overall score is sum of all section scores
    scores['OVERALL'] = Object.values(scores).reduce((sum, score) => sum + score, 0);
    
    console.log('📊 ScoreCard: Extracted scores from data:', scores);
    return scores;
  };

  const processScoreData = (testData, sections, student, allStudentTestsOrdered) => {
    // Clean the student PIN by removing 'student_' prefix if present
    const cleanStudentPin = student.startsWith('student_') 
      ? student.replace('student_', '') 
      : student;
    
    console.log('📊 ScoreCard: Processing score data', {
      selectedTest,
      originalStudent: student,
      cleanStudentPin,
      percentileDataAvailable: !!percentileData,
      percentileDataStructure: percentileData ? Object.keys(percentileData) : 'none',
      percentileDataContent: percentileData,
      testDataLength: testData.length,
      sampleTestData: testData.slice(0, 2),
      availableColumns: testData.length > 0 ? Object.keys(testData[0]) : [],
      hasStudentMarks: testData.length > 0 && testData[0].student_marks !== undefined,
      studentFieldExists: testData.length > 0 && cleanStudentPin in testData[0],
      dataSource: testData.length > 0 && testData[0].student_marks !== undefined ? 'MyIMSAdmin (pre-calculated)' : 'Calculated from responses'
    });
    
    const testResults = calculateAllTestPercentilesAndScores(cleanStudentPin);
    
    // Handle both test name formats: "testId" and "Test_testId"
    let currentPercentiles = testResults.percentiles[selectedTest];
    let currentScores = testResults.scores[selectedTest];
    
    // If not found with selectedTest, try with "Test_" prefix
    if (!currentPercentiles && !currentScores) {
      const testWithPrefix = `Test_${selectedTest}`;
      currentPercentiles = testResults.percentiles[testWithPrefix] || {};
      currentScores = testResults.scores[testWithPrefix] || {};
    } else {
      currentPercentiles = currentPercentiles || {};
      currentScores = currentScores || {};
    }
    
    // CRITICAL: Use pre-calculated percentiles from percentileData prop (from backend)
    // This data comes from MyIMSAdmin database, not calculated locally
    if (percentileData) {
      console.log('📊 ScoreCard: Using PRE-CALCULATED percentile data from backend:', percentileData);
      
      // Map API percentile structure to frontend expected format
      // API returns: { overall, qa, varc, dilr }
      const actualPercentiles = {};
      
      if (percentileData.overall !== undefined && percentileData.overall !== null) {
        actualPercentiles['OVERALL'] = percentileData.overall;
      }
      if (percentileData.qa !== undefined && percentileData.qa !== null) {
        actualPercentiles['QA'] = percentileData.qa;
      }
      if (percentileData.varc !== undefined && percentileData.varc !== null) {
        actualPercentiles['VARC'] = percentileData.varc;
      }
      if (percentileData.dilr !== undefined && percentileData.dilr !== null) {
        actualPercentiles['DILR'] = percentileData.dilr;
      }
      
      console.log('📊 ScoreCard: Mapped percentiles:', actualPercentiles);
      
      // OVERRIDE with actual percentiles instead of calculated ones
      currentPercentiles = actualPercentiles;
    } else {
      console.warn('⚠️ ScoreCard: No percentileData from backend, using calculated values (may be 0)');
    }
    
    console.log('🔍 ScoreCard Debug:', {
      selectedTest,
      testWithPrefix: `Test_${selectedTest}`,
      availableTests: Object.keys(testResults.scores),
      currentScores,
      currentPercentiles
    });
    
    setPercentiles(currentPercentiles);

    const elaborateResults = sections.map(section => {
      const sectionQs = testData.filter(q => q.section === section);
      console.log(`🔍 ScoreCard: Section ${section} processing:`, {
        sectionQsLength: sectionQs.length,
        cleanStudentPin,
        sampleQuestion: sectionQs[0],
        studentColumn: sectionQs[0] ? sectionQs[0][cleanStudentPin] : 'No questions',
        availableStudentColumns: sectionQs[0] ? Object.keys(sectionQs[0]).filter(k => k.includes('STU')) : [],
        sampleResponses: sectionQs.slice(0, 3).map(q => ({ question: q.question_number, response: q[cleanStudentPin], responseType: typeof q[cleanStudentPin] }))
      });
      
      const attemptedQs = sectionQs.filter(q => q[cleanStudentPin] && typeof q[cleanStudentPin] === 'string' && q[cleanStudentPin]?.toLowerCase() !== "skipped");
      const correctQs = attemptedQs.filter(q => q[cleanStudentPin]?.toLowerCase() === "correct");
      
      console.log(`📊 ScoreCard: Section ${section} response analysis:`, {
        totalQuestions: sectionQs.length,
        attemptedQuestions: attemptedQs.length,
        correctQuestions: correctQs.length,
        sampleAttemptedResponses: attemptedQs.slice(0, 3).map(q => ({ question: q.question_number, response: q[cleanStudentPin] })),
        sampleCorrectResponses: correctQs.slice(0, 3).map(q => ({ question: q.question_number, response: q[cleanStudentPin] }))
      });
      const accuracy = attemptedQs.length > 0 ? ((correctQs.length / attemptedQs.length) * 100).toFixed(1) : "0.0";
      const score = currentScores[section] !== undefined ? currentScores[section] : 0;
      return {
        section,
        questions: sectionQs.length,
        attempts: attemptedQs.length,
        correct: correctQs.length,
        incorrect: attemptedQs.filter(q => q[cleanStudentPin]?.toLowerCase() === "incorrect").length,
        skipped: sectionQs.length - attemptedQs.length,
        accuracy,
        score,
        percentile: currentPercentiles[section] || "0.0"
      };
    });

    const allQsAttempted = testData.filter(q => q[cleanStudentPin] && typeof q[cleanStudentPin] === 'string' && q[cleanStudentPin]?.toLowerCase() !== "skipped");
    const allCorrect = allQsAttempted.filter(q => q[cleanStudentPin]?.toLowerCase() === "correct");
    const overallScore = currentScores['OVERALL'] !== undefined ? currentScores['OVERALL'] : 0;
    const overallAccuracy = allQsAttempted.length > 0 ? ((allCorrect.length / allQsAttempted.length) * 100).toFixed(1) : "0.0";

    const overall = {
      section: "OVERALL",
      questions: testData.length,
      attempts: allQsAttempted.length,
      correct: allCorrect.length,
      incorrect: testData.filter(q => q[cleanStudentPin] && typeof q[cleanStudentPin] === 'string' && q[cleanStudentPin]?.toLowerCase() === "incorrect").length,
      skipped: testData.length - allQsAttempted.length,
      accuracy: overallAccuracy,
      score: overallScore,
      percentile: currentPercentiles['OVERALL'] || "0.0"
    };
    setElaborateData([...elaborateResults, overall]);

    const catResults = sections.map(section => ({
      sectionName: section === "QA" ? "Quantitative Ability" : section === "VARC" ? "Verbal Ability" : "DILR",
      score: currentScores[section] || 0,
      percentile: currentPercentiles[section] || "0.0"
    }));
    setCatReplicaData([...catResults, {
      sectionName: "Overall",
      score: overallScore,
      percentile: currentPercentiles['OVERALL'] || "0.0"
    }]);

    setGraphicData({
      overall: { score: overallScore, percentile: currentPercentiles['OVERALL'] || "0.0" },
      sections: sections.map(section => ({
        sectionName: section === "QA" ? "Quantitative" : section === "VARC" ? "Verbal" : "DILR",
        score: currentScores[section] || 0,
        percentile: currentPercentiles[section] || "0.0"
      }))
    });
    
    if (elaborateResults.length > 0 && overall) {
        generateAllAIInsights(elaborateResults, overall, selectedTest, testResults, allStudentTestsOrdered);
    }
  };

  // Enhanced context generation based on actual test attempt dates (chronological)
  const getPerformanceContext = async (sectionPerformanceData, overallPerformanceData, currentTestName, allTestResults, allStudentTestsOrdered) => {
    console.log('🧠 ScoreCard: Generating chronological context based on attempt dates');
    
    const startTime = Date.now();
    
    try {
      // Get historical tests based on actual attempt dates
      const studentPin = selectedStudent.replace('student_', '');
      console.log('📅 ScoreCard: Fetching chronological historical tests for:', studentPin, currentTestName);
      
      const response = await fetch(`${API_BASE_URL}/student/${studentPin}/historical-tests/${currentTestName}`);
      
      if (!response.ok) {
        console.log('⚠️ ScoreCard: No historical tests found, using basic context');
        const basicContext = getBasicPerformanceContext(sectionPerformanceData, overallPerformanceData, currentTestName, allTestResults, allStudentTestsOrdered);
        
        // Update debug info
        setDebugInfo({
          historicalTests: [],
          contextString: basicContext,
          apiResponseTime: Date.now() - startTime,
          currentTestDate: currentTestAttemptDate,
          isFirstTest: true,
          error: 'No historical API response'
        });
        
        return basicContext;
      }
      
      const historicalData = await response.json();
      const chronologicalTests = historicalData.historicalTests || [];
      
      console.log(`📅 ScoreCard: Found ${chronologicalTests.length} chronological historical tests`);
      
      // Build chronological context
      const isFirstTest = chronologicalTests.length === 0;
      
      let promptContent = `Student Performance Analysis for Test: ${currentTestName}.\n`;
      promptContent += isFirstTest 
        ? "This is the student's first recorded test based on chronological attempt dates.\n" 
        : `This is test number ${chronologicalTests.length + 1} in chronological order. Analyzed against ${chronologicalTests.length} previous test(s).\n`;
      
      // Add current test attempt date context if available
      if (currentTestAttemptDate) {
        promptContent += `Test attempted on: ${currentTestAttemptDate}.\n`;
      }
      
      // Add chronological context for historical tests
      if (chronologicalTests.length > 0) {
        promptContent += "\nCHRONOLOGICAL TEST HISTORY:\n";
        chronologicalTests.forEach((test, index) => {
          const testName = getTestNameSafely(test.testId);
          promptContent += `${index + 1}. ${testName} - Attempted: ${test.formattedDate}\n`;
        });
      }
      
      promptContent += "\nSection-wise Performance:\n";
      sectionPerformanceData.forEach(sec => {
        const level = getPerformanceLevel(sec.percentile);
        const cutoffStatus = categoryCutoffs?.[sec.section?.toLowerCase()] != null
          ? (sec.score >= categoryCutoffs[sec.section.toLowerCase()] ? 'CLEARED' : 'MISSED')
          : 'UNKNOWN';
        promptContent += `- ${sec.section}: Score ${sec.score}, Percentile ${sec.percentile}% (${level}). Cutoff ${cutoffStatus}.\n`;
      });
      
      promptContent += "\nOverall Performance:\n";
      const overallLevel = getPerformanceLevel(overallPerformanceData.percentile);
      const overallCutoffStatus = categoryCutoffs?.overall != null
        ? (overallPerformanceData.score >= categoryCutoffs.overall ? 'CLEARED' : 'MISSED')
        : 'UNKNOWN';
      promptContent += `- Overall: Score ${overallPerformanceData.score}, Percentile ${overallPerformanceData.percentile}% (${overallLevel}). Cutoff ${overallCutoffStatus}.\n`;
      
      // Add comparison with most recent previous test (chronologically)
      if (!isFirstTest && chronologicalTests.length > 0) {
        const mostRecentTest = chronologicalTests[chronologicalTests.length - 1];
        const recentTestName = getTestNameSafely(mostRecentTest.testId);
        
        // Try to get previous test data for comparison
        const previousScores = allTestResults.scores[mostRecentTest.testId];
        const previousPercentiles = allTestResults.percentiles[mostRecentTest.testId];
        
        if (previousScores && previousPercentiles) {
          promptContent += `\nCOMPARISON WITH MOST RECENT PREVIOUS TEST (${recentTestName} - ${mostRecentTest.formattedDate}):\n`;
          
          const percentileChange = parseFloat(overallPerformanceData.percentile) - parseFloat(previousPercentiles['OVERALL'] || 0);
          promptContent += `- Overall percentile change: ${percentileChange > 0 ? '+' : ''}${percentileChange.toFixed(1)} points\n`;
          
          // Section-wise comparison
          sectionPerformanceData.forEach(sec => {
            const prevSectionPercentile = parseFloat(previousPercentiles[sec.section] || 0);
            const sectionPercentileChange = parseFloat(sec.percentile) - prevSectionPercentile;
            if (prevSectionPercentile > 0) {
              promptContent += `- ${sec.section} percentile change: ${sectionPercentileChange > 0 ? '+' : ''}${sectionPercentileChange.toFixed(1)} points\n`;
            }
          });
        } else {
          promptContent += `\nPREVIOUS TEST: Most recent was ${recentTestName} on ${mostRecentTest.formattedDate}\n`;
        }
      }
      
      promptContent += "\nInstructions for AI: Generate insights based on this chronological context data. Focus on meaningful progress analysis considering the actual sequence of test attempts by date.";
      
      // Update debug info with successful data
      setDebugInfo({
        historicalTests: chronologicalTests,
        contextString: promptContent,
        apiResponseTime: Date.now() - startTime,
        currentTestDate: currentTestAttemptDate,
        isFirstTest,
        error: null
      });
      
      return promptContent;
      
    } catch (error) {
      console.error('❌ ScoreCard: Error generating chronological context:', error);
      
      // Fallback to basic context generation
      const basicContext = getBasicPerformanceContext(sectionPerformanceData, overallPerformanceData, currentTestName, allTestResults, allStudentTestsOrdered);
      
      // Update debug info with error
      setDebugInfo({
        historicalTests: [],
        contextString: basicContext,
        apiResponseTime: Date.now() - startTime,
        currentTestDate: currentTestAttemptDate,
        isFirstTest: null,
        error: error.message
      });
      
      return basicContext;
    }
  };
  
  // Fallback basic context generation (used when chronological context fails)
  const getBasicPerformanceContext = (sectionPerformanceData, overallPerformanceData, currentTestName, allTestResults, allStudentTestsOrdered) => {
    console.log('📋 ScoreCard: Using fallback basic context generation');
    
    // Note: This is a fallback method when chronological context is not available
    // The main method now uses actual attempt dates for proper historical context
    
    const currentTestIndex = allStudentTestsOrdered.indexOf(currentTestName);
    const isFirstTest = currentTestIndex === 0;

    let promptContent = `Student Performance Analysis for Test: ${currentTestName}.\n`;
    promptContent += "Note: Using basic context (chronological data unavailable).\n";
    promptContent += isFirstTest 
        ? "This appears to be the student's first test based on available data.\n" 
        : `This is test ${currentTestIndex + 1} based on available data order.\n`;

    promptContent += "\nSection-wise Performance:\n";
    sectionPerformanceData.forEach(sec => {
        const level = getPerformanceLevel(sec.percentile);
        const cutoffStatus = categoryCutoffs?.[sec.section?.toLowerCase()] != null
          ? (sec.score >= categoryCutoffs[sec.section.toLowerCase()] ? 'CLEARED' : 'MISSED')
          : 'UNKNOWN';
        promptContent += `- ${sec.section}: Score ${sec.score}, Percentile ${sec.percentile}% (${level}). Cutoff ${cutoffStatus}.\n`;
    });

    promptContent += "\nOverall Performance:\n";
    const overallLevel = getPerformanceLevel(overallPerformanceData.percentile);
    const overallCutoffStatus = categoryCutoffs?.overall != null
      ? (overallPerformanceData.score >= categoryCutoffs.overall ? 'CLEARED' : 'MISSED')
      : 'UNKNOWN';
    promptContent += `- Overall: Score ${overallPerformanceData.score}, Percentile ${overallPerformanceData.percentile}% (${overallLevel}). Cutoff ${overallCutoffStatus}.\n`;
    
    promptContent += "\nInstructions for AI: Generate insights based on this basic context data. Note that chronological historical analysis was not available.";
    return promptContent;
  };

  const buildSystemPromptForAI = () => {
    const customPrompt = `Analyze the student's test performance based on the provided data. Generate 4-5 insights that meaningfully combine the following data points:
- Percentage of questions attempted overall and per section
- Accuracy percentage overall and per section
- Percentile and score
- Assigned level in each section
- Whether the student has cleared or missed the cutoff (overall and per section)
- Comparison with previous test(s), if data is available
- Strength areas (strong accuracy, high score, cleared cutoff, etc.)
- Weak areas or red flags (low attempts, poor accuracy, missed cutoffs, etc.)

Use "you" instead of "the student" in all statements. Use numbers from the data to substantiate observations. Do not call anything excellent if it is less than 90 percentile. Keep the language simple, objective, and non-conversational. The output should be only bullet points in plain text, no headings or summaries. Do not exceed 5 plain text bullet points`;
    
    return `You are an expert CAT exam performance analyzer. ${customPrompt}
        Guidelines:
        1. Format as bullet points starting with "• " 
        2. Acknowledge if this is the first test. If not, mention comparison to previous tests.
        3. Highlight which cutoffs were cleared/missed (don't mention specific scores in insights, just "cleared" or "missed").
        4. Compare to previous tests *only if this isn't the first test* and relevant comparison data is provided in the user prompt. Refer to improvement, decline, or consistency.
        5. If it's the best performance so far (score or percentile) based on user prompt, highlight it.
        6. Keep language straightforward and direct.
        7. Do not mention specific percentile numbers or scores in the bullet points; use performance levels (Learner, Capable, Advanced, Ninja) and general comparison terms.
        8. Mention specific test names only when relevant for comparison (e.g., "previous best was in SimCAT X"), if provided in user context.

        Preferred Style Examples (emulate this style):
        ${SAMPLE_INSIGHTS_FOR_TRAINING.join('\n\n')}
        
        Use the detailed context provided in the USER prompt to form your bullet points. Do not add any conversational fluff.
      `;
  };

  // Unified approach for AI calls: backend expects 'prompt' and 'model'
  // ScoreCard.jsx

// Enhanced AI insight generation with caching support
const generateAIInsight = async (aiProvider, performanceContextString, modelName, loadingKey, insightKey, forceRegenerate = false) => {
  setLoadingAI(prev => ({ ...prev, [loadingKey]: true }));
  
  try {
      const modelTypeForStorage = aiProvider;
      const skillType = "score_insights";

      // Step 1: Check cache first (unless force regenerate)
      if (!forceRegenerate) {
        const cachedSummary = aiSummaryCacheService.getCachedSummary(
          selectedStudent,
          selectedTest,
          skillType,
          modelTypeForStorage
        );
        
        if (cachedSummary && cachedSummary.content) {
          console.log(`✅ ScoreCard: Using cached summary for ${aiProvider}`);
          setAiInsights(prev => ({ ...prev, [insightKey]: cachedSummary.content }));
          setLoadingAI(prev => ({ ...prev, [loadingKey]: false }));
          return;
        }
      }

      // Step 2: No cache found or force regenerate - generate fresh insights
      console.log(`📝 ScoreCard: Generating fresh AI insights for ${aiProvider}`);
      
      // Use basic system prompt for better performance
      const basicSystemPrompt = buildSystemPromptForAI();
      
      const combinedPrompt = `${basicSystemPrompt}

USER_CONTEXT_START
${performanceContextString}
USER_CONTEXT_END`;

      const res = await fetch(`${API_BASE_URL}/generate/${aiProvider}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
              prompt: combinedPrompt,
              model: modelName,
              temperature: 0.4, 
              max_tokens: 400,
          })
      });

      if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.error || `${aiProvider.toUpperCase()} request failed: ${res.statusText}`);
      }
      
      const data = await res.json();
      let text = data.choices?.[0]?.message?.content || `No insights from ${aiProvider}.`;
      
      // Step 3: Update UI with generated content
      setAiInsights(prev => ({ ...prev, [insightKey]: text }));
      
      // Step 4: Cache the generated summary for future use
      const cached = aiSummaryCacheService.cacheSummary(
        selectedStudent,
        selectedTest,
        skillType,
        modelTypeForStorage,
        text,
        {
          testName: getTestNameSafely(selectedTest),
          attemptDate: currentTestAttemptDate,
          generatedAt: new Date().toISOString()
        }
      );
      
      if (cached) {
        console.log(`💾 ScoreCard: Summary cached successfully for ${aiProvider}`);
      }

  } catch (error) {
      console.error(`${aiProvider.toUpperCase()} Error (enhanced):`, error);
      setAiInsights(prev => ({ ...prev, [insightKey]: `Error: ${error.message}` }));
  } finally {
      setLoadingAI(prev => ({ ...prev, [loadingKey]: false }));
  }
};
  

const generateDeepSeekInsights = (performanceContextString, forceRegenerate = false) => // ADDED forceRegenerate
  generateAIInsight('deepseek', performanceContextString, 'deepseek-chat', 'deepseek', 'deepseek', forceRegenerate); // PASS forceRegenerate
  
  // ScoreCard.jsx

const generateAllAIInsights = async (sectionData, overallData, testName, testResults, allStudentTestsOrdered, forceRegenerate = false) => {
  if (!sectionData || !overallData || !testName || !testResults || !allStudentTestsOrdered) {
      console.warn("Missing data for AI insight generation.");
      return;
  }
  
  try {
    const performanceContextString = await getPerformanceContext(sectionData, overallData, testName, testResults, allStudentTestsOrdered);
    if (!performanceContextString) {
        console.warn("Failed to generate performance context string.");
        return;
    }

    // Generate insights with caching support
    console.log("Generating AI insights with caching...", forceRegenerate ? "(Force regenerate)" : "(Using cache if available)");
    await generateDeepSeekInsights(performanceContextString, forceRegenerate);
    
  } catch (error) {
    console.error('❌ Error in AI insights generation:', error);
    setAiInsights({ 
      deepseek: "Error generating insights. Please try again." 
    });
  }
};

  const getPerformanceLevel = (percentile) => {
    const p = parseFloat(percentile);
    if (isNaN(p)) return "Learner";
    if (p >= 99) return "Ninja";
    if (p >= 95) return "Advanced";
    if (p >= 80) return "Capable";
    return "Learner";
  };



  // Helper to get correct cutoff key for progress bar
  const getCutoffKeyForProgressBar = (sectionName) => {
    if (sectionName === "Quantitative") return "QA";
    if (sectionName === "Verbal") return "VARC";
    if (sectionName === "DILR") return "DILR";
    if (sectionName === "Overall Performance") return "OVERALL"; // Assuming "Overall Performance" is used for display
    return sectionName.toUpperCase(); // Fallback, should ideally match categoryCutoffs keys
  };

  const ProgressBar = ({ score, cutoff, max = 100 }) => {
    const s = parseFloat(score);
    const c = parseFloat(cutoff);
    const m = parseFloat(max);

    if (isNaN(s) || isNaN(c) || isNaN(m) || m === 0) {
        // Display a disabled-looking bar or text for N/A
        return (
            <div className="relative h-4 bg-gray-300 rounded-full mb-1 overflow-hidden flex items-center justify-center">
                <span className="text-xs text-gray-500">N/A</span>
            </div>
        );
    }

    const percentage = Math.min(Math.max((s / m) * 100, 0), 100);
    const isAboveCutoff = s >= c;
    const cutoffPosition = Math.min(Math.max((c / m) * 100, 0), 100);

    return (
      <div className="relative h-4 bg-gray-200 rounded-full mb-1 overflow-hidden">
        <div 
          className={`absolute h-full rounded-l-full ${isAboveCutoff ? 'bg-green-500' : 'bg-red-500'}`}
          style={{ width: `${percentage}%` }}
        ></div>
        <div 
            className="absolute top-0 bottom-0 w-0.5 bg-slate-600 z-10"
            style={{ left: `${cutoffPosition}%` }}
            title={`Cutoff: ${c.toFixed(0)}`}
        ></div>
      </div>
    );
  };
  
  const isLoadingAnyAI = loadingAI.deepseek;

  return (
    <div className="p-4 sm:p-6 md:p-8 bg-gray-50 min-h-screen">
      <h2 className="text-2xl sm:text-3xl font-bold text-center mb-6 sm:mb-8 text-indigo-700">📊 Scorecard</h2>

      {/* Test Attempt Date Section */}
      {selectedStudent && selectedTest && (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-6">
          <h3 className="text-lg sm:text-xl font-semibold mb-3 text-indigo-600">Test Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center">
              <span className="font-medium text-gray-700 mr-2">📅 Test:</span>
              <span className="text-gray-900">{getTestNameSafely(selectedTest)}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium text-gray-700 mr-2">📅 Attempt Date:</span>
              <span className={`text-gray-900 ${currentTestAttemptDate ? '' : 'italic text-gray-500'}`}>
                {currentTestAttemptDate || 'Date not available'}
              </span>
            </div>
          </div>
          

        </div>
      )}

      {selectedStudent && selectedTest && elaborateData.length > 0 && (
        <div className="space-y-6 sm:space-y-8">


          {/* TYPE 1: Elaborate */}
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
            <h3 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6 text-ims-primary">Performance Overview</h3>

            {/* DESKTOP: Full Table */}
            <div className="hidden md:block overflow-x-auto shadow-sm rounded-lg border border-gray-200">
              <table className="min-w-full table-auto text-sm">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="p-3 border-b text-left text-ims-text-secondary uppercase tracking-wider first:pl-4 text-center">SECTION</th>
                    <th className="p-3 border-b text-left text-ims-text-secondary uppercase tracking-wider text-center">QNS</th>
                    <th className="p-3 border-b text-left text-ims-text-secondary uppercase tracking-wider text-center">ATT</th>
                    <th className="p-3 border-b text-left text-ims-text-secondary uppercase tracking-wider text-center">CORR</th>
                    <th className="p-3 border-b text-left text-ims-text-secondary uppercase tracking-wider text-center">INCORR</th>
                    <th className="p-3 border-b text-left text-ims-text-secondary uppercase tracking-wider text-center">SKIP</th>
                    <th className="p-3 border-b text-left text-ims-text-secondary uppercase tracking-wider text-center" title="Accuracy of attempted questions">
                      ACC% <span className="text-xs">ℹ️</span>
                    </th>
                    <th className="p-3 border-b text-left text-ims-text-secondary uppercase tracking-wider text-center">SCORE</th>
                    <th className="p-3 border-b text-left text-ims-text-secondary uppercase tracking-wider text-center">%ILE</th>
                    <th className="p-3 border-b text-left text-ims-text-secondary uppercase tracking-wider last:pr-4 text-center">LEVEL</th>
                  </tr>
                </thead>
                <tbody>
                  {elaborateData.map((row, i) => (
                    <tr key={i} className={`hover:bg-blue-50/50 ${row.section === "OVERALL" ? "bg-blue-100 font-bold text-blue-800" : "text-ims-text-primary"}`}>
                      <td className="p-3 border-b border-r pl-4">{row.section}</td>
                      <td className="p-3 border-b border-r text-center">{row.questions}</td>
                      <td className="p-3 border-b border-r text-center">{row.attempts}</td>
                      <td className="p-3 border-b border-r text-center">{row.correct}</td>
                      <td className="p-3 border-b border-r text-center">{row.incorrect}</td>
                      <td className="p-3 border-b border-r text-center">{row.skipped}</td>
                      <td className="p-3 border-b border-r text-center">{row.accuracy}%</td>
                      <td className="p-3 border-b border-r text-center">{row.score}</td>
                      <td className="p-3 border-b border-r text-center">{row.percentile}</td>
                      <td className="p-3 border-b text-center pr-4">{getPerformanceLevel(row.percentile)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* MOBILE: Card Layout */}
            <div className="md:hidden space-y-3">
              {elaborateData.map((row, i) => (
                <div
                  key={i}
                  className={`border border-gray-200 rounded-xl overflow-hidden ${
                    row.section === "OVERALL" ? "bg-blue-50 border-l-4 border-l-ims-primary" : "bg-white"
                  }`}
                >
                  {/* Card Header */}
                  <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center">
                    <span className={`font-semibold ${row.section === "OVERALL" ? "text-blue-800" : "text-ims-text-primary"}`}>
                      {row.section}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      row.section === "OVERALL" ? "bg-ims-primary text-white" : "bg-gray-100 text-ims-text-secondary"
                    }`}>
                      {getPerformanceLevel(row.percentile)}
                    </span>
                  </div>

                  {/* Card Body - Key Metrics */}
                  <div className="px-4 py-3 grid grid-cols-3 gap-3">
                    <div className="text-center">
                      <p className="text-xs text-ims-text-muted">Score</p>
                      <p className="text-lg font-bold text-ims-text-primary">{row.score}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-ims-text-muted">%ILE</p>
                      <p className="text-lg font-bold text-ims-text-primary">{row.percentile}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-ims-text-muted">Accuracy</p>
                      <p className="text-lg font-bold text-ims-text-primary">{row.accuracy}%</p>
                    </div>
                  </div>

                  {/* Card Footer - Detailed Stats */}
                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                    <div className="grid grid-cols-4 gap-2 text-center text-xs">
                      <div>
                        <p className="text-ims-text-muted">QNS</p>
                        <p className="font-medium text-ims-text-primary">{row.questions}</p>
                      </div>
                      <div>
                        <p className="text-ims-text-muted">ATT</p>
                        <p className="font-medium text-ims-text-primary">{row.attempts}</p>
                      </div>
                      <div>
                        <p className="text-ims-text-muted">CORR</p>
                        <p className="font-medium text-ims-success">{row.correct}</p>
                      </div>
                      <div>
                        <p className="text-ims-text-muted">INCORR</p>
                        <p className="font-medium text-ims-error">{row.incorrect}</p>
                      </div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between text-xs">
                      <span className="text-ims-text-muted">Skipped: <span className="font-medium text-ims-text-primary">{row.skipped}</span></span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* TYPE 2: CAT Replica */}
          {catReplicaData.length > 0 && (
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
                <h3 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6 text-ims-primary">CAT Replica Score Summary</h3>

                {/* DESKTOP: Full Table */}
                <div className="hidden md:block overflow-x-auto shadow-sm rounded-lg border border-gray-200">
                <table className="min-w-full table-auto text-sm">
                    <thead className="bg-blue-50 text-ims-text-secondary uppercase tracking-wider">
                    <tr>
                        {catReplicaData.map((section, i) => (
                        <th key={i} className="p-3 border-b" colSpan={2}>
                            {section.sectionName}
                        </th>
                        ))}
                    </tr>
                    <tr>
                        {catReplicaData.map((_, i) => (
                        <React.Fragment key={i}>
                            <th className="p-3 border-b text-center">Score</th>
                            <th className="p-3 border-b text-center">Percentile</th>
                        </React.Fragment>
                        ))}
                    </tr>
                    </thead>
                    <tbody className="text-ims-text-primary">
                    <tr>
                        {catReplicaData.map((section, i) => (
                        <React.Fragment key={i}>
                            <td className="p-3 border-b border-r text-center font-medium">{section.score}</td>
                            <td className={`p-3 border-b text-center font-medium ${i === catReplicaData.length - 1 ? '' : 'border-r'}`}>{section.percentile}</td>
                        </React.Fragment>
                        ))}
                    </tr>
                    </tbody>
                </table>
                </div>

                {/* MOBILE: Card Layout */}
                <div className="md:hidden space-y-3">
                  {catReplicaData.map((section, i) => (
                    <div key={i} className="bg-white border border-gray-200 rounded-xl p-4">
                      <div className="flex justify-between items-center mb-3">
                        <span className="font-semibold text-ims-text-primary">{section.sectionName}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center bg-blue-50 rounded-lg p-3">
                          <p className="text-xs text-ims-text-secondary uppercase">Score</p>
                          <p className="text-xl font-bold text-ims-primary">{section.score}</p>
                        </div>
                        <div className="text-center bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-ims-text-secondary uppercase">Percentile</p>
                          <p className="text-xl font-bold text-ims-text-primary">{section.percentile}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
            </div>
          )}

          {/* TYPE 3: Graphic */}
          {graphicData && (() => {
            console.log('📊 ScoreCard Graphical Summary: Using cutoffs:', {
              categoryCutoffs,
              studentCategory,
              overall: categoryCutoffs?.overall,
              fallbackOverall: null // No hardcoded fallback — cutoff must come from categoryCutoffs
            });
            return (
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
              <h3 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6 text-indigo-600">Graphical Performance Summary</h3>
              <div className="mb-6">
                <div className="flex justify-between mb-1 text-xs sm:text-sm">
                  <span className="font-medium text-gray-800">Overall Performance</span>
                  <span className="text-gray-600">
                    Score: {graphicData.overall.score} | Cutoff: {categoryCutoffs?.overall ?? 'N/A'} | %ile: {graphicData.overall.percentile}
                  </span>
                </div>
                <ProgressBar 
                  score={graphicData.overall.score} 
                  cutoff={categoryCutoffs?.overall ?? 0}
                  max={198} 
                />
              </div>
              <div className="space-y-4">
                {graphicData.sections.map((section, i) => {
                  const cutoffKey = getCutoffKeyForProgressBar(section.sectionName);
                  const sectionCutoff = categoryCutoffs?.[cutoffKey.toLowerCase()] ?? 0;
                  
                  return (
                    <div key={i}>
                      <div className="flex justify-between mb-1 text-xs sm:text-sm">
                        <span className="font-medium text-gray-800">{section.sectionName}</span>
                        <span className="text-gray-600">
                          Score: {section.score} | Cutoff: {sectionCutoff} | %ile: {section.percentile}
                        </span>
                      </div>
                      <ProgressBar 
                        score={section.score} 
                        cutoff={sectionCutoff}
                        max={section.sectionName === "Quantitative" ? 66 : (section.sectionName === "Verbal" ? 72 : 60) }
                      />
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 flex items-center text-xs sm:text-sm text-gray-500">
                <div className="w-2.5 h-2.5 bg-green-500 mr-1.5 rounded-sm"></div>
                <span>Above cutoff</span>
                <div className="w-2.5 h-2.5 bg-red-500 mx-1.5 ml-3 rounded-sm"></div>
                <span>Below cutoff</span>
                <div className="w-0.5 h-3 bg-slate-600 mx-1.5 ml-3"></div>
                <span>Cutoff Mark</span>
                {categoryCutoffs && studentCategory && (
                  <span className="ml-4 text-indigo-600 font-medium">
                    ({studentCategory} Category)
                  </span>
                )}
              </div>
            </div>
            );
          })()}

        </div>
      )}
       {selectedStudent && selectedTest && elaborateData.length === 0 && !isLoadingAnyAI && (
        <div className="mt-10 text-center text-gray-500 p-6 bg-white shadow rounded-lg text-sm sm:text-base">
          <p>No score data available for the selected student and test.</p>
          <p className="text-xs mt-1">Please verify that the student has attempted this test and data is available in the database.</p>
        </div>
      )}
       {!selectedStudent && !selectedTest && (
         <div className="mt-10 text-center text-gray-500 p-6 bg-white shadow rounded-lg text-sm sm:text-base">
            Please select a student and a test to view the score card.
         </div>
      )}
    </div>
  );
};

export default ScoreCard;